"use client";

import BaseSlider from "@/components/BaseSlider/BaseSlider";
import * as C from "@/components";
import { useState } from "react";
import { singularSpecifications } from "./singularSpecifications";
import VadImage from "vadimages-nextjs-image-optimizer";

import css from "./vape-strategy-swiper.module.scss";

interface SpecificationPropertyProps {
  label: string;
  keyPoints: string[];
}

interface SpecificationProperties {
  activation?: SpecificationPropertyProps;
  features?: SpecificationPropertyProps;
  specs?: SpecificationPropertyProps;
  construction?: SpecificationPropertyProps;
  batteryFeatures?: SpecificationPropertyProps;
  flavorSettings?: SpecificationPropertyProps;
  customization?: SpecificationPropertyProps;
  automation?: SpecificationPropertyProps;
  technicalDetails?: SpecificationPropertyProps;
}

interface SpecificationCardProps {
  title: string;
  specs: SpecificationProperties;
}

const SingularAccordion = ({
  label,
  children,
  isOpen,
  blockId,
  setActiveBlock,
}: {
  label: string;
  children: React.ReactNode;
  isOpen: boolean;
  blockId: number;
  setActiveBlock: (_: number) => void;
}) => {
  const toggleButton = () => {
    setActiveBlock(isOpen ? -1 : blockId);
  };

  return (
    <div className="border-b last:border-none py-5 text-new-black-domina">
      <button
        className="flex items-center justify-between w-full text-[14px] font-semibold mb-1"
        type="button"
        onClick={toggleButton}
      >
        <span>{label ?? "hui"}</span>
        {isOpen ? <span>-</span> : <span>+</span>}
      </button>
      {isOpen && children}
    </div>
  );
};

interface SpecificationCardWrapperProps {
  card: SpecificationCardProps;
  activeBlocks: number[];
  cardBlockId: number;
  setActiveBlocks: (_: number[]) => void;
}

const SpecificationCard = ({
  activeBlocks,
  cardBlockId,
  card,
  setActiveBlocks,
}: SpecificationCardWrapperProps) => {
  const changeActiveBlock = (blockId?: number) => {
    const newActiveBlocks = [...activeBlocks];
    newActiveBlocks[cardBlockId] = blockId !== undefined ? blockId : -1;

    setActiveBlocks(newActiveBlocks);
  };

  const { title } = card;

  const {
    activation,
    features,
    specs,
    construction,
    batteryFeatures,
    customization,
    automation,
    technicalDetails,
    flavorSettings,
  } = card.specs;

  const rawAccordions = [
    technicalDetails,
    activation,
    features,
    flavorSettings,
    specs,
    construction,
    batteryFeatures,
    customization,
    automation,
  ];

  const accordions = rawAccordions.filter(
    (accordion) =>
      accordion !== undefined &&
      accordion?.keyPoints !== undefined &&
      accordion?.label !== undefined
  );

  const cardImageName = title
    .replaceAll(":", "")
    .replaceAll(" ", "-")
    .toLowerCase();

  return (
    <C.Card className="p-10 h-full">
      <div className="h-[330px] mb-10 p-10">
        <VadImage
          src={`/images/singular/specifications/${cardImageName}.png`}
          alt=""
          width={448}
          height={330}
          className="max-w-full max-h-full object-contain"
        />
      </div>
      <h5>{title}</h5>
      {accordions.map((accordion, accordionId) => (
        <SingularAccordion
          key={`specification-${card.title}-${accordion?.label}`}
          isOpen={accordionId === activeBlocks[cardBlockId]}
          setActiveBlock={changeActiveBlock}
          blockId={accordionId}
          label={accordion?.label ?? "ERR: Check the specification file"}
        >
          <ul className="space-y-1">
            {accordion?.keyPoints.map((keyPoint, keyPointIdx) => (
              <li
                key={`${accordion.label}-key-point-${keyPointIdx}`}
                dangerouslySetInnerHTML={{ __html: keyPoint }}
                className="relative translate-x-[1.25rem] before:content-[''] before:absolute before:w-2 before:h-2 before:bg-golden-lemon before:top-2 before:-left-4 [&>ul]:list-disc [&>ul]:translate-x-[1.25rem]"
              />
            ))}
          </ul>
        </SingularAccordion>
      ))}
    </C.Card>
  );
};

const SingularSpecificationsSlider = () => {
  const [activeBlocks, setActiveBlocks] = useState<number[]>([-1, -1, -1, -1, -1]);

  // Same config as the live Swiper (FreeMode/Mousewheel/Scrollbar/Navigation), minus Swiper
  // itself: BaseSlider is now a SnapSlider adapter with a draggable scrollbar + prev/next arrows
  // (positioned by vape-strategy-swiper.module.scss). The old per-toggle height recalculation
  // (recalculationDependencyList) is gone: flexbox items-stretch equalizes card heights
  // automatically as accordions open/close.
  return (
    <BaseSlider
      className={`container ${css['slider-wrapper']}`}
      sliderProps={{
        slidesPerView: 1.2,
        spaceBetween: 24,
        navigation: true,
        freeMode: true,
        breakpoints: {
          1024: {
            slidesPerView: 2.4,
            spaceBetween: 24,
          },
        },
      }}
    >
      {singularSpecifications.map((card, cardIdx) => (
        <SpecificationCard
          key={`spec-card-${card.title}`}
          card={card}
          cardBlockId={cardIdx}
          activeBlocks={activeBlocks}
          setActiveBlocks={setActiveBlocks}
        />
      ))}
    </BaseSlider>
  );
};

export default SingularSpecificationsSlider;
